import React, { ReactNode, createContext, useContext } from "react";
import { ClientRepository, InvoiceRepository, VetRepository, PetRepository, ActivityRepository, SettingsRepository, ReportRepository } from "./interfaces";
import { DatastoreClientRepository } from "./datastore/ClientRepository";
import { DatastoreInvoiceRepository } from "./datastore/InvoiceRepository";
import { DatastoreVetRepository } from "./datastore/VetRepository";
import { DatastorePetRepository } from "./datastore/PetRepository";
import { DatastoreActivityRepository } from "./datastore/ActivityRepository";
import { DatastoreSettingsRepository } from "./datastore/SettingsRepository";
import { DatastoreReportRepository } from "./datastore/ReportRepository";
import { inMemoryRepositories } from "./inmemory";
import { isE2EMode } from "../e2eMode";

interface RepositoryContextValue {
  clients: ClientRepository;
  invoices: InvoiceRepository;
  vets: VetRepository;
  pets: PetRepository;
  activities: ActivityRepository;
  settings: SettingsRepository;
  reports: ReportRepository;
}

export const RepositoryContext = createContext<RepositoryContextValue>(null!);

// In e2e mode (REACT_APP_E2E=true) the app runs against in-memory
// repositories so Playwright tests don't need an AWS backend. The value is a
// module-level singleton because the provider is mounted more than once and
// every mount must resolve to the same repositories.
const repositories: RepositoryContextValue = isE2EMode
  ? inMemoryRepositories
  : {
      clients: new DatastoreClientRepository(),
      invoices: new DatastoreInvoiceRepository(),
      vets: new DatastoreVetRepository(),
      pets: new DatastorePetRepository(),
      activities: new DatastoreActivityRepository(),
      settings: new DatastoreSettingsRepository(),
      reports: new DatastoreReportRepository(),
    };

export const DataStoreRepositoryProvider: React.FC<{ children: ReactNode }> = ({ children }) => (
  <RepositoryContext.Provider value={repositories}>{children}</RepositoryContext.Provider>
);

export const useRepositories = () => useContext(RepositoryContext);
